import React, { useEffect, useState, useRef } from "react";
import "./App.css";
import { ReactTagManager } from "react-gtm-ts";

const tagManagerArgs = {
  code: "GTM-WZQPMC7",
};

type CursorShapes = {
  [key: string]: string[];
  Sparkles: string[];
  Glitter: string[];
  Stars: string[];
  Rain: string[];
  Autumn: string[];
  Winter: string[];
  Party: string[];
  Love: string[];
};

type AnimationTypes = {
  [key: string]: string[];
  Default: string[];
  Rotations: string[];
  ShakeAndSwing: string[];
  Directions: string[];
  Wind: string[];
};

type BestCombinations = {
  [key: string]: string[];
  Rain: string[];
  Autumn: string[];
  Winter: string[];
  Party: string[];
  Love: string[];
};

const filenameMap: { [key: string]: string } = {
  // Sparkles
  goldSparkles: "gold-sparkle",
  silverSparkles: "silver-sparkle",
  colorSparkles: "color-sparkle",
  blueSparkles: "blue-sparkle",
  
  // Glitter
  glitter: "glitter",
  colorGlitter: "color-glitter",
  
  // Stars
  goldStars: "gold-star",
  silverStars: "silver-star",
  colorStars: "color-star",
  blueStars: "blue-star",
  blueStars2: "blue-moon",
  goldStars2: "gold-moon",
  
  // Rain
  goldRain: "gold-rain",
  silverRain: "silver-rain",
  colorRain: "color-rain",
  blueRain: "blue-rain",
  goldDrops: "gold-drop",
  silverDrops: "silver-drop",
  blueDrops: "blue-drop",
  colorDrops: "color-drop",
  
  // Autumn
  leaves: "leave",
  flowers: "flower",
  spiral: "spiral",
  goldFeather: "gold-feather",
  
  // Winter
  flakes1: "flake-1",
  flakes2: "flake-2",
  colorXmas: "xmas-ball",
  blueXmas: "xmas-ball-2",
  goldXmas: "xmas-ball-3",
  snow1: "white-snow",
  snow2: "blue-snow",
  snow3: "gold-snow",
  
  // Party
  confetti1: "confetti",
  confetti2: "confetti-2",
  party: "ice-cream",
  
  // Love
  idealLove: "heart-balloon",
  love: "heart-red",
  goldLove: "heart-gold",
  springLove: "flower"
};

const meta: {
  CursorShapes: CursorShapes;
  AnimationTypes: AnimationTypes;
  BestCombinations: BestCombinations;
} = {
  CursorShapes: {
    Sparkles: [
      "goldSparkles",
      "silverSparkles",
      "colorSparkles",
      "blueSparkles",
    ],
    Glitter: ["glitter", "colorGlitter"],
    Stars: [
      "goldStars",
      "silverStars",
      "colorStars",
      "blueStars",
      "blueStars2",
      "goldStars2",
    ],
    Rain: [
      "goldRain",
      "silverRain",
      "colorRain",
      "blueRain",
      "goldDrops",
      "silverDrops",
      "blueDrops",
      "colorDrops",
    ],
    Autumn: ["leaves", "flowers", "spiral", "goldFeather"],
    Winter: [
      "flakes1",
      "flakes2",
      "colorXmas",
      "blueXmas",
      "goldXmas",
      "snow1",
      "snow2",
      "snow3",
    ],
    Party: ["confetti1", "confetti2", "party"],
    Love: ["idealLove", "love", "goldLove", "springLove"],
  },
  AnimationTypes: {
    Default: ["flash"],
    Rotations: ["rotation90", "rotation90n", "rotation360", "rotation360n"],
    ShakeAndSwing: ["shake", "shakeX", "shakeY", "swing"],
    Directions: [
      "down",
      "downScale",
      "downRotate1",
      "downRotate2",
      "leftDownScale",
      "rightDownScale",
      "leftDown",
      "rightDown",
      "up",
    ],
    Wind: ["wind", "storm", "gale", "gust"],
  },
  BestCombinations: {
    Rain: [
      "down",
      "downScale",
      "downRotate1",
      "downRotate2",
      "leftDownScale",
      "rightDownScale",
      "leftDown",
      "rightDown",
      "up",
    ],
    Autumn: ["wind", "storm", "gale", "gust"],
    Winter: [
      "flash",
      "rotation90",
      "rotation90n",
      "rotation360",
      "rotation360n",
      "down",
      "leftDown",
      "rightDown",
      "wind",
      "storm",
      "gale",
      "gust",
    ],
    Party: [
      "flash",
      "rotation90",
      "rotation90n",
      "rotation360",
      "rotation360n",
      "shake",
      "shakeX",
      "shakeY",
      "swing",
      "down",
      "leftDown",
      "rightDown",
      "up",
    ],
    Love: [
      "flash",
      "rotation90",
      "rotation90n",
      "rotation360",
      "rotation360n",
      "shake",
      "shakeX",
      "shakeY",
      "swing",
      "down",
      "leftDown",
      "rightDown",
      "up",
    ],
  },
};

function App() {
  ReactTagManager.init(tagManagerArgs);
  const intercomInitialized = useRef(false);
  const BASE_URL = "https://wix.certifiedcode.io/ecwid-cursor-trail";
  const [instance, setInstance] = React.useState({} as any);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedEffectType, setSelectedEffectType] = React.useState(0);
  const [selectedEffect, setSelectedEffect] = React.useState("");
  const [selectedAnimationType, setSelectedAnimationType] = React.useState(0);
  const [selectedAnimation, setSelectedAnimation] = React.useState("");
  const [shouldSaveSettings, setShouldSaveSettings] = useState(false);

  useEffect(() => {
    if (!intercomInitialized.current && instance.intercomToken && instance.storeProfile && instance.app) {
      const name = instance.storeProfile.account.accountName;
      const registrationDate = new Date(
        instance.storeProfile.account.registrationDate
      );
      const created_at = Math.floor(registrationDate.getTime() / 1000);
      const phone = instance.storeProfile.company.phone;
      const email = instance.storeProfile.account.accountEmail;
      const utm_source = instance.app.client_id;
      const options = {
        email,
        name,
        created_at,
        phone,
        user_hash: instance.intercomToken,
        utm_source,
      };
      window.Intercom("boot", options);
      intercomInitialized.current = true;
    }
  }, [instance]);

  useEffect(() => {
    try {
      window.EcwidApp.init({
        app_id: "cursor-trail",
        autoloadedflag: true,
        autoheight: false,
      });

      getInstance();
      
      const updateHeight = () => {
        var body = document.body,
          html = document.documentElement;

        var height = Math.max(
          body.scrollHeight,
          body.offsetHeight,
          html.clientHeight,
          html.scrollHeight,
          html.offsetHeight
        );

        window.EcwidApp.setSize({ height: height });
      };

      updateHeight();

      const instanceIntervalId = setInterval(getInstance, 10000);
      const heightIntervalId = setInterval(updateHeight, 1000);

      return () => {
        clearInterval(instanceIntervalId);
        clearInterval(heightIntervalId);
      };
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    if (instance?.store_id) {
      getSettings();
    }
  }, [instance?.store_id]);

  useEffect(() => {
    if (selectedEffect && selectedAnimation) {
      try {
        window.cursorTrail({
          pattern: selectedEffect,
          animationType: selectedAnimation,
          theme: "light",
        });
      } catch (error) {
        console.log(error);
      }
    }
  }, [selectedEffect, selectedAnimation]);

  function getInstance() {
    fetch(
      BASE_URL + "/_functions/@certifiedcode/ecwid-repluggable-backend/auth",
      {
        headers: {
          Authorization: new URLSearchParams(window.location.search).get(
            "payload"
          )!,
        },
      }
    )
      .then((response) => response.json())
      .then((payload) => {
        setInstance(payload);
        window.EcwidApp.ready();
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  }

  function getSettings() {
    fetch(BASE_URL + "/_functions/@certifiedcode/ecwid-repluggable-backend/settings", {
      method: "GET",
      headers: {
        Authorization: new URLSearchParams(window.location.search).get(
          "payload"
        )!,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        console.log(data);
        setIsEnabled(data.isEnabled);
        if (data?.config?.animationType) {
          setSelectedAnimation(data.config.animationType);
          setSelectedAnimationType(
            Object.keys(meta.AnimationTypes).findIndex((key) =>
              meta.AnimationTypes[key].includes(data.config.animationType)
            )
          );
        }
        if (data?.config?.pattern) {
          setSelectedEffect(data.config.pattern);
          setSelectedEffectType(
            Object.keys(meta.CursorShapes).findIndex((key) =>
              meta.CursorShapes[key].includes(data.config.pattern)
            )
          );
        }
      })
      .catch(() => {});
  }

  const handleEffectChange = (effect: string) => {
    setSelectedEffect(effect);
    setShouldSaveSettings(true);
  };

  const handleAnimationChange = (animation: string) => {
    setSelectedAnimation(animation);
    setShouldSaveSettings(true);
  };

  const handleToggle = () => {
    if (!instance?.subscription) {
      window.open(
        instance?.storeProfile
          ? encodeURI(
              "https://www.certifiedcode.us/upgrade/apps?checkout=" +
                JSON.stringify({
                  settings: {
                    allowLogout: false,
                    allowDiscountRemoval: false,
                  },
                  items: [{ priceId: "pri_01jm9wqp8a473jxxr01ykb9m4g", quantity: 1 }],
                  customer: {
                    email: instance.storeProfile.account.accountEmail,
                    business: { name: instance.storeProfile.account.accountName },
                    address: {
                      countryCode: instance.storeProfile.company.countryCode,
                      postalCode: instance.storeProfile.company.postalCode,
                      city: instance.storeProfile.company.city,
                    },
                  },
                  customData: { store_id: `${instance.store_id}` },
                })
            )
          : undefined,
        "_blank"
      );
    } else {
      setIsLoading(true);
      const newEnabledState = !isEnabled;
      fetch(BASE_URL + "/_functions/@certifiedcode/ecwid-repluggable-backend/settings", {
        method: "POST",
        headers: {
          Authorization: new URLSearchParams(window.location.search).get(
            "payload"
          )!,
        },
        body: JSON.stringify({
          isEnabled: newEnabledState,
          config: {
            pattern: selectedEffect,
            animationType: selectedAnimation,
            theme: "light",
          },
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          console.log(data);
          setIsEnabled(data.isEnabled);
          if (data?.config?.animationType) {
            setSelectedAnimation(data.config.animationType);
            setSelectedAnimationType(
              Object.keys(meta.AnimationTypes).findIndex((key) =>
                meta.AnimationTypes[key].includes(data.config.animationType)
              )
            );
          }
          if (data?.config?.pattern) {
            setSelectedEffect(data.config.pattern);
            setSelectedEffectType(
              Object.keys(meta.CursorShapes).findIndex((key) =>
                meta.CursorShapes[key].includes(data.config.pattern)
              )
            );
          }
        })
        .catch((error) => {
          console.error("Error updating settings:", error);
          setIsEnabled(!newEnabledState);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  useEffect(() => {
    if (!isLoading && selectedEffect && selectedAnimation && shouldSaveSettings) {
      const timeoutId = setTimeout(() => {
        setSettings();
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [selectedEffect, selectedAnimation, shouldSaveSettings]);

  function setSettings() {
    setIsLoading(true);
    fetch(BASE_URL + "/_functions/@certifiedcode/ecwid-repluggable-backend/settings", {
      method: "POST",
      headers: {
        Authorization: new URLSearchParams(window.location.search).get(
          "payload"
        )!,
      },
      body: JSON.stringify({
        isEnabled: isEnabled,
        config: {
          pattern: selectedEffect,
          animationType: selectedAnimation,
          theme: "light",
        },
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        console.log(data);
        setIsEnabled(data.isEnabled);
        if (data?.config?.animationType) {
          setSelectedAnimation(data.config.animationType);
          setSelectedAnimationType(
            Object.keys(meta.AnimationTypes).findIndex((key) =>
              meta.AnimationTypes[key].includes(data.config.animationType)
            )
          );
        }
        if (data?.config?.pattern) {
          setSelectedEffect(data.config.pattern);
          setSelectedEffectType(
            Object.keys(meta.CursorShapes).findIndex((key) =>
              meta.CursorShapes[key].includes(data.config.pattern)
            )
          );
        }
        setShouldSaveSettings(false);
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  }

  return (
    <div className="settings-page cf">
      <div className="settings-page__header">
        <div className="settings-page__titles settings-page__titles--left">
          <h1 className="settings-page__title">Cursor Trail Effects</h1>
          <div className="settings-page__subtitle">Add beautiful cursor trail effects to enhance your store's visual experience</div>
        </div>

        <div className="named-area">
          <div className="named-area__header">
            <div className="named-area__titles">
              <div className="named-area__title">1. Choose Your Effect</div>
            </div>
            <div className="named-area__description">
              Select a cursor trail effect from our collection
            </div>
          </div>
          <div className="named-area__body">
            <div className="a-card a-card--normal">
              <div className="a-card__paddings">
                <div className="tabs">
                  {Object.keys(meta.CursorShapes).map((key, index) => (
                    <div
                      key={key}
                      className={`tab ${selectedEffectType === index ? 'active' : ''}`}
                      onClick={() => setSelectedEffectType(index)}
                    >
                      {key}
                    </div>
                  ))}
                </div>
                <div className="grid">
                  {meta.CursorShapes[
                    Object.keys(meta.CursorShapes)[selectedEffectType]
                  ].map((effect: string) => (
                    <div
                      key={effect}
                      className={`grid-item ${selectedEffect === effect ? 'selected' : ''}`}
                      onClick={() => handleEffectChange(effect)}
                    >
                      <img src={`/patterns/${filenameMap[effect]}.svg`} alt={effect} />
                      <div className="grid-item-title">
                        {effect
                          .replace(/([A-Z])/g, " $1")
                          .trim()
                          .split(" ")
                          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                          .join(" ")}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="named-area">
          <div className="named-area__header">
            <div className="named-area__titles">
              <div className="named-area__title">2. Choose Animation</div>
            </div>
            <div className="named-area__description">
              Select how your cursor trail effect should animate
            </div>
          </div>
          <div className="named-area__body">
            <div className="a-card a-card--normal">
              <div className="a-card__paddings">
                <div className="tabs">
                  {Object.keys(meta.AnimationTypes).map((key, index) => (
                    <div
                      key={key}
                      className={`tab ${selectedAnimationType === index ? 'active' : ''}`}
                      onClick={() => setSelectedAnimationType(index)}
                    >
                      {key}
                    </div>
                  ))}
                </div>
                <div className="grid">
                  {meta.AnimationTypes[
                    Object.keys(meta.AnimationTypes)[selectedAnimationType]
                  ].map((animation: string) => (
                    <div
                      key={animation}
                      className={`grid-item ${selectedAnimation === animation ? 'selected' : ''}`}
                      onClick={() => handleAnimationChange(animation)}
                    >
                      <div className="grid-item-title">
                        {animation
                          .replace(/([A-Z])/g, " $1")
                          .trim()
                          .split(" ")
                          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                          .join(" ")}
                      </div>
                      {meta.BestCombinations[
                        Object.keys(meta.CursorShapes)[selectedEffectType]
                      ]?.includes(animation) && (
                        <div className="recommended-badge">Recommended</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {selectedEffect && selectedAnimation && (
          <div className="named-area">
            <div className="named-area__header">
              <div className="named-area__titles">
                <div className="named-area__title">3. Enable Effect</div>
              </div>
              <div className="named-area__description">
                {!instance?.subscription 
                  ? 'Upgrade to enable your selected cursor trail effect'
                  : 'Your selections are ready! Toggle to enable the cursor trail effect'
                }
              </div>
            </div>
            <div className="named-area__body">
              <div className="a-card a-card--normal">
                <div className="a-card__paddings">
                  <div className="upgrade-element">
                    <div className="upgrade-element__core">
                      <div className="upgrade-element__data">
                        <div className="upgrade-element__title">Current Configuration</div>
                        <div className="upgrade-element__content">
                          <div className="settings-grid">
                            <div className="settings-item">
                              <div className="settings-item__label">Selected Effect</div>
                              <div className="settings-item__value">
                                <div className="selected-effect">
                                  <img 
                                    src={`/patterns/${filenameMap[selectedEffect]}.svg`} 
                                    alt={selectedEffect} 
                                    className="selected-effect__icon"
                                  />
                                  <span>
                                    {selectedEffect
                                      .replace(/([A-Z])/g, " $1")
                                      .trim()
                                      .split(" ")
                                      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                                      .join(" ")}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="settings-item">
                              <div className="settings-item__label">Animation Type</div>
                              <div className="settings-item__value">
                                <span>
                                  {selectedAnimation
                                    .replace(/([A-Z])/g, " $1")
                                    .trim()
                                    .split(" ")
                                    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                                    .join(" ")}
                                </span>
                              </div>
                            </div>
                            {meta.BestCombinations[
                              Object.keys(meta.CursorShapes)[selectedEffectType]
                            ]?.includes(selectedAnimation) && (
                              <div className="settings-item">
                                <div className="settings-item__label">Compatibility</div>
                                <div className="settings-item__value">
                                  <span className="badge badge--success">Recommended Combination</span>
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="upgrade-element__action" style={{ marginTop: '16px' }}>
                            {!instance?.subscription ? (
                              <button 
                                className="button button--primary"
                                onClick={handleToggle}
                                style={{ backgroundColor: '#2964de', color: 'white', padding: '12px 24px' }}
                              >
                                Upgrade to Enable
                              </button>
                            ) : (
                              <label className="checkbox big">
                                <input 
                                  type="checkbox" 
                                  checked={isEnabled}
                                  onChange={handleToggle}
                                />
                                <div>
                                  <div></div>
                                </div>
                              </label>
                            )}
                          </div>
                        </div>
                        <div className="upgrade-element__note">
                          <div>
                            {!instance?.subscription 
                              ? 'Upgrade to enable cursor trail effects'
                              : isEnabled ? 'Click to disable cursor trail effect' : 'Click to enable cursor trail effect'
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="named-area">
          <div className="named-area__header">
            <div className="named-area__titles">
              <div className="named-area__title">Key Features</div>
            </div>
            <div className="named-area__description">
              Discover how our cursor trail effects can enhance your store's visual experience
            </div>
          </div>
          <div className="named-area__body">
            <div className="a-card a-card--normal">
              <div className="a-card__paddings">
                <div>
                  <div className="promo-row">
                    <div className="promo-row__content">
                      <ul className="titled-items-list">
                        <li className="titled-item titled-items-list__item">
                          <div className="titled-item__title">Beautiful Effects</div>
                          <div className="titled-item__content">
                            Choose from a variety of stunning cursor trail effects
                          </div>
                        </li>
                        <li className="titled-item titled-items-list__item">
                          <div className="titled-item__title">Smooth Animations</div>
                          <div className="titled-item__content">
                            Customize how your effects animate with different animation types
                          </div>
                        </li>
                        <li className="titled-item titled-items-list__item">
                          <div className="titled-item__title">Performance Optimized</div>
                          <div className="titled-item__content">
                            Enjoy smooth cursor trails without impacting your store's performance
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
